import { defineStore } from 'pinia'
import type { Product, ProductWithInfo } from '@/types/product'
import type { ProductData } from '@/helpers/productPlacementHelper'
import type { FabricImage } from 'fabric'
import type { backdrop } from '@/types/backdrop'
import type { background } from '@/types/background'
import type { AIShadowProperties } from '@/types/aiShadows'

export interface TiltStrategy {
  name: string
  value: string
}

interface EditorState {
  createdBackDrops: Array<backdrop>
  createdBackGrounds: Array<background>
  selectedBackDropIndex: number | null
  selectedBackGroundIndex: number | null
  baseSelectedBackDropIndex: number | null
  baseBackDrops: Array<backdrop>
  baseSelectedBackGroundIndex: number | null
  baseCreatedBackGrounds: Array<background>
  baseSelectedBackGroundOption: number
  // Text content
  headline: string
  subheadline: string
  selectedBackgroundOption: number

  // Product management
  activeProducts: Product[]
  activeProductDataItems: { productData: ProductData; fabricImage: FabricImage }[]
  // Advanced Settings - Shadow
  shadowOpacity: number
  shadowScale: number
  shadowLightingIndex: number
  isDropShadow: boolean
  isAiShadow: boolean
  glowColor: string | null
  isGlowShadowApplied: boolean

  // Advanced Settings - Product Scaling
  groupScaleX: number
  groupScaleY: number
  increaseProductSize: boolean
  decreaseProductSize: boolean

  // Advanced Settings - Product Tilt
  selectedTiltStrategy: TiltStrategy | null
  productCount: number

  // Advanced Settings - System
  syncingShadow: boolean
  specialIndex: Array<{ index: number, specialIndex: number, normalIndex: number }>

  // Lock Layer Functionality
  isPlacementLocked: boolean
  isBackgroundLocked: boolean
  isHeadlineLocked: boolean
  isSubheadlineLocked: boolean
  isLogoLocked: boolean

  // UI State
  enableAdvancedSettings: boolean
  isShufflingPlacement: boolean
  isRegenerating: boolean
  writingHeadlineUsingAI: boolean
  writingSubheadlineUsingAI: boolean
  headlinePromptMenu: boolean
  subheadlinePromptMenu: boolean
  isShuffleLoading: boolean

  // Private properties for recent prompts
  recentHeadlinePrompt: string
  recentSubHeadlinePrompt: string
  selectedBackdrop: string
  selectedBackground: string
  isAutoFix: boolean
  autofixBackgroundType: string
  isGlowShadowTriggeredFromUndo: boolean,
  updatedCanvasData: any,
  shouldUpdateCanvasData: boolean
  contrastRatio: number
}

export const useEditorStore = defineStore('editor', {
  state: (): EditorState => ({
    // Text content
    headline: '',
    subheadline: '',
    selectedBackgroundOption: 0,

    // Product management
    activeProducts: [],
    activeProductDataItems: [],
    createdBackDrops: [],
    createdBackGrounds: [],
    selectedBackDropIndex: null,
    selectedBackGroundIndex: null,
    baseSelectedBackDropIndex: 0,
    baseBackDrops: [],
    baseSelectedBackGroundIndex: 0,
    baseCreatedBackGrounds: [],
    baseSelectedBackGroundOption: 0,

    // Advanced Settings - Shadow
    shadowOpacity: 0.4,
    shadowScale: 1,
    shadowLightingIndex: 0,
    isDropShadow: false,
    isAiShadow: false,
    glowColor: null,
    isGlowShadowApplied: false,

    // Advanced Settings - Product Scaling
    groupScaleX: 1,
    groupScaleY: 1,
    increaseProductSize: true,
    decreaseProductSize: true,

    // Advanced Settings - Product Tilt
    selectedTiltStrategy: null,
    productCount: 1,

    // Advanced Settings - System
    syncingShadow: false,
    specialIndex: [],

    // Lock Layer Functionality
    isPlacementLocked: false,
    isBackgroundLocked: false,
    isHeadlineLocked: true,
    isSubheadlineLocked: true,
    isLogoLocked: false,

    // UI State
    enableAdvancedSettings: false,
    isShufflingPlacement: false,
    isRegenerating: false,
    writingHeadlineUsingAI: false,
    writingSubheadlineUsingAI: false,
    headlinePromptMenu: false,
    subheadlinePromptMenu: false,
    recentHeadlinePrompt: '',
    recentSubHeadlinePrompt: '',
    isShuffleLoading: false,
    selectedBackdrop: '',
    selectedBackground: '',
    isAutoFix: false,
    isGlowShadowTriggeredFromUndo: false,
    updatedCanvasData: null,
    shouldUpdateCanvasData: false,
    autofixBackgroundType: '',
    contrastRatio: 0
  }),

  actions: {
    // Text content actions
    setHeadline(headline: string) {
      this.headline = headline
    },
    setSubheadline(subheadline: string) {
      this.subheadline = subheadline
    },

    // Product management actions
    setActiveProducts(products: Product[]) {
      this.activeProducts = products
    },
    setSelectedBackgroundOption(selectedBackgroundOption: number) {
      this.selectedBackgroundOption = selectedBackgroundOption
    },
    storeShadowProperties(productId: number, aiShadowProperties: AIShadowProperties): void {
      const product = this.activeProducts.find((p) => p.id === productId) as ProductWithInfo

      if (product) {
        product.productInfo = product.productInfo || {}
        product.productInfo.shadowInfo = aiShadowProperties
      }
    },
    addBackDrops(
      backdrop: backdrop,
      action: string = 'append',
      index: number = 0
    ) {
      if (action === 'prepend') {
        this.createdBackDrops.unshift(backdrop)
      }
      else if (action === 'insert') {
        this.createdBackDrops.splice(index, 0, backdrop)
      }
      else {
        this.createdBackDrops.push(backdrop)
      }
    },
    removeBackDrops(index: number) {
      this.createdBackDrops.splice(index, 1)
    },
    removeBackGrounds(index: number) {
      this.createdBackGrounds.splice(index, 1)
    },
    prependBackGrounds(background: background) {
      this.createdBackGrounds.unshift(background)
    },
    setSelectedBackDrop(index: number | null) {
      this.selectedBackDropIndex = index
    },
    setSelectedBackGround(index: number | null) {
      this.selectedBackGroundIndex = index
    },
    resetEditorStore() {
      this.createdBackDrops = []
      this.createdBackGrounds = []
      this.selectedBackDropIndex = null
      this.selectedBackGroundIndex = null
      this.specialIndex = []
      this.recentHeadlinePrompt = ''
      this.recentSubHeadlinePrompt = ''
      this.enableAdvancedSettings = false
      this.isGlowShadowApplied = false
      this.glowColor = null
      this.isGlowShadowTriggeredFromUndo = false
      this.resetAllLocks()
    },

    // Advanced Settings - Shadow actions
    setShadowOpacity(opacity: number) {
      this.shadowOpacity = opacity
    },
    setShadowScale(scale: number) {
      this.shadowScale = scale
    },
    setShadowLightingIndex(index: number) {
      this.shadowLightingIndex = index
    },
    setIsDropShadow(isDropShadow: boolean) {
      this.isDropShadow = isDropShadow
    },
    setIsAiShadow(isAiShadow: boolean) {
      this.isAiShadow = isAiShadow
    },
    setGlowColor(color: string | null) {
      this.glowColor = color
    },
    setIsGlowShadowApplied(isApplied: boolean) {
      this.isGlowShadowApplied = isApplied
    },

    // Advanced Settings - Product Scaling actions
    setGroupScaleX(scaleX: number) {
      this.groupScaleX = scaleX
    },
    setGroupScaleY(scaleY: number) {
      this.groupScaleY = scaleY
    },
    setIncreaseProductSize(canIncrease: boolean) {
      this.increaseProductSize = canIncrease
    },
    setDecreaseProductSize(canDecrease: boolean) {
      this.decreaseProductSize = canDecrease
    },

    // Advanced Settings - Product Tilt actions
    setSelectedTiltStrategy(strategy: TiltStrategy | null) {
      this.selectedTiltStrategy = strategy
    },
    setProductCount(count: number) {
      this.productCount = count
    },

    // Advanced Settings - System actions
    setSyncingShadow(syncing: boolean) {
      this.syncingShadow = syncing
    },

    // Lock Layer Functionality actions
    setPlacementLocked(locked: boolean) {
      this.isPlacementLocked = locked
    },
    setBackgroundLocked(locked: boolean) {
      this.isBackgroundLocked = locked
    },
    setHeadlineLocked(locked: boolean) {
      this.isHeadlineLocked = locked
    },
    setSubheadlineLocked(locked: boolean) {
      this.isSubheadlineLocked = locked
    },
    setLogoLocked(locked: boolean) {
      this.isLogoLocked = locked
    },
    togglePlacementLocked() {
      this.isPlacementLocked = !this.isPlacementLocked
    },
    toggleBackgroundLocked() {
      this.isBackgroundLocked = !this.isBackgroundLocked
    },
    toggleHeadlineLocked() {
      this.isHeadlineLocked = !this.isHeadlineLocked
    },
    toggleSubheadlineLocked() {
      this.isSubheadlineLocked = !this.isSubheadlineLocked
    },
    toggleLogoLocked() {
      this.isLogoLocked = !this.isLogoLocked
    },
    resetAllLocks() {
      this.isPlacementLocked = false
      this.isBackgroundLocked = false
      this.isHeadlineLocked = true
      this.isSubheadlineLocked = true
      this.isLogoLocked = false
    },

    // UI State setters
    setEnableAdvancedSettings(enabled: boolean) {
      this.enableAdvancedSettings = enabled
    },

    setIsShufflingPlacement(shuffling: boolean) {
      this.isShufflingPlacement = shuffling
    },

    setIsRegenerating(regenerating: boolean) {
      this.isRegenerating = regenerating
    },

    setWritingHeadlineUsingAI(writing: boolean) {
      this.writingHeadlineUsingAI = writing
    },

    setWritingSubheadlineUsingAI(writing: boolean) {
      this.writingSubheadlineUsingAI = writing
    },

    setHeadlinePromptMenu(open: boolean) {
      this.headlinePromptMenu = open
    },

    setSubheadlinePromptMenu(open: boolean) {
      this.subheadlinePromptMenu = open
    },

    // Bulk state update for undo/redo
    updateAdvancedSettingsState(state: Partial<EditorState>) {
      Object.assign(this, state)
    },

    setIsGlowShadowTriggeredFromUndo(isTriggeredFromUndo: boolean) {
      this.isGlowShadowTriggeredFromUndo = isTriggeredFromUndo
    },
    setUpdatedCanvasData(canvasData: any) {
      this.updatedCanvasData = canvasData
    },
    setShouldUpdateCanvasData(shouldUpdate: boolean) {
      this.shouldUpdateCanvasData = shouldUpdate
    }
  },
})
